<template>
    <div class="tour-manager">
        <!-- Toolbar -->
        <table-toolbar
            :show-title="true"
            title="Tours"
            :show-refresh="true"
            @refresh="loadTours"
        >
            <template #action-buttons>
                <themed-button 
                    v-if="canManageTours"
                    @click="showCreateModal = true"
                    variant="default"
                    size="sm"
                >
                    Create Tour
                </themed-button>
            </template>
        </table-toolbar>

        <!-- Content -->
        <div v-if="loading" class="tour-manager__loading">
            Loading tours...
        </div>

        <div v-else-if="tours.length === 0" class="tour-manager__empty">
            <p>No tours yet. Create your first tour to get started.</p>
        </div>

        <!-- Grid -->
        <themed-grid
            v-else
            :data="tours"
            :fields="tourFields"
            :actions="tourActions"
            :columns="3"
            @action="handleAction"
        />


        <tour-form-modal
            v-if="showCreateModal || showEditModal"
            :tour="selectedTour"
            :project-id="projectId"
            :available-comps="availableComps"
            :available-sites="availableSites"
            @close="closeModals"
            @saved="handleTourSaved"
        />



        <tour-scoring
            v-if="showScoringModal"
            :tour="selectedTour"
            @close="closeScoringModal"
            @completed="loadTours"
        />

        <tour-results
            v-if="showResultsModal"
            :tour="selectedTour"
            @tour-updated="selectedTour = $event"
            @close="closeResultsModal"
        />
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import ThemedButton from '../ThemedButton.vue';
import ThemedGrid from '../ThemedGrid.vue';
import TableToolbar from '../table/TableToolbar.vue';

// KAN-173: Async imports — these are large components (1327, 545, ~300 lines)
// that are only needed when a user clicks Edit/Score/Results
const TourFormModal = () => import('./TourFormModal.vue');
const TourScoring   = () => import('./TourScoring.vue');
const TourResults   = () => import('./TourResults.vue');

export default {
    name: 'TourManager',

    components: {
        ThemedButton,
        ThemedGrid,
        TableToolbar,
        TourFormModal,
        TourScoring,
        TourResults,
    },

    props: {
        projectId: {
            type: Number,
            required: true,
        },
        availableComps: {
            type: Array,
            default: () => [],
        },
        availableSites: {
            type: Array,
            default: () => [],
        },
        canManageTours: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            tours: [],
            loading: false,
            showCreateModal: false,
            showEditModal: false,
            showScoringModal: false,
            showResultsModal: false,
            selectedTour: null,
        };
    },

    computed: {
        tourFields() {
            const fields = [
                // Title
                { field: 'name', type: 'title' },
                // Subtitle
                { field: 'description', type: 'subtitle' },
                // Status badge (top-right)
                {
                    field: 'status',
                    type: 'badge',
                    position: 'topRight',
                    classFunction: (tour) => {
                        const variants = {
                            draft: 'badge--secondary',
                            active: 'badge--green',
                            completed: 'badge--primary',
                            cancelled: 'badge--red',
                        };
                        return variants[tour.status] || 'badge--secondary';
                    }
                },
                // Date field
                {
                    field: 'date',
                    label: 'Date',
                    type: 'text',
                    transform: (value) => value ? moment(value).format('MMM D, YYYY h:mm A') : 'Not scheduled'
                },
                // Site field
                {
                    field: 'site',
                    label: 'Site',
                    type: 'text',
                    transform: (value) => {
                        if (!value) return 'No site assigned';
                        
                        // Try building name first
                        if (value.building) {
                            if (value.building.building_name) return value.building.building_name;
                            if (value.building.address_line_1) return value.building.address_line_1;
                        }
                        
                        // Fallback to site properties
                        if (value.name) return value.name;
                        if (value.site_name) return value.site_name;
                        
                        return `Site #${value.id}`;
                    }
                },
            ];
            
            // Add broker field for admins
            if (this.$gate && this.$gate.isAdmin()) {
                fields.push({
                    field: 'creator',
                    label: 'Assigned Broker',
                    type: 'text',
                    transform: (value) => value ? value.name : 'No broker assigned'
                });
            }
            
            // Two-column row for Comps and Attendees
            fields.push({
                columns: 2,
                fields: [
                    {
                        field: 'tour_comps',
                        label: 'Comps',
                        type: 'number',
                        transform: (value) => {
                            if (!value) return 0;
                            // Only count actual comp visits, exclude lunch and drive slots
                            return value.filter(tc => tc.slot_type === 'comp' || !tc.slot_type).length;
                        }
                    },
                    {
                        field: 'attendees',
                        label: 'Attendees',
                        type: 'number',
                        transform: (value) => value ? value.length : 0
                    }
                ]
            });
            
            return fields;
        },

        tourActions() {
            return [
                {
                    name: 'edit',
                    label: 'Edit',
                    icon: 'edit-icon',
                    variant: 'default',
                    size: 'sm',
                    visible: () => this.canManageTours
                },
                {
                    name: 'score',
                    label: 'Score Comps (Demo)',
                    icon: 'star',
                    variant: 'secondary',
                    size: 'sm',
                    visible: () => this.canManageTours
                },
                {
                    name: 'results',
                    label: 'View Results',
                    icon: 'eye',
                    variant: 'secondary',
                    size: 'sm',
                    visible: () => this.canManageTours
                }
            ];
        }
    },

    mounted() {
        this.loadTours();
    },

    methods: {
        async loadTours() {
            this.loading = true;
            try {
                const response = await axios.get(`/projects/${this.projectId}/tours`);
                this.tours = response.data;
            } catch (error) {
                console.error('Error loading tours:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load tours');
                }
            } finally {
                this.loading = false;
            }
        },

        handleAction({ action, item: tour }) {
            switch (action) {
                case 'edit':
                    this.editTour(tour);
                    break;
                case 'score':
                    this.startScoring(tour);
                    break;
                case 'results':
                    this.viewResults(tour);
                    break;
            }
        },

        editTour(tour) {
            this.selectedTour = tour;
            this.showEditModal = true;
        },

        startScoring(tour) {
            this.selectedTour = tour;
            this.showScoringModal = true;
        },

        viewResults(tour) {
            this.selectedTour = tour;
            this.showResultsModal = true;
        },

        isAttendee(tour) {
            if (!tour.attendees) return false;
            if (!window.Laravel || !window.Laravel.user) return false;
            const currentUserId = window.Laravel.user.id;
            return tour.attendees.some(a => a.user_id === currentUserId);
        },

        closeModals() {
            this.showCreateModal = false;
            this.showEditModal = false;
            this.showScoringModal = false;
            this.showResultsModal = false;
            this.$nextTick(() => {
                this.selectedTour = null;
            });
        },

        closeScoringModal() {
            this.showScoringModal = false;
            this.$nextTick(() => {
                this.selectedTour = null;
            });
        },

        closeResultsModal() {
            this.showResultsModal = false;
            this.$nextTick(() => {
                this.selectedTour = null;
            });
        },

        handleTourSaved() {
            this.closeModals();
            this.loadTours();
            if (this.$toast) {
                this.$toast.success('Tour saved successfully');
            }
        },

        formatDate(date) {
            return moment(date).format('MMM D, YYYY h:mm A');
        },

        formatTime(time) {
            return moment(time).format('h:mm A');
        },

        formatStatus(status) {
            const statuses = {
                draft: 'Draft',
                active: 'Active',
                completed: 'Completed',
                cancelled: 'Cancelled',
            };
            return statuses[status] || status;
        },

        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },

        calculateTotalDistance(tour) {
            if (!tour.tour_comps) return '0 mi';
            const total = tour.tour_comps.reduce((sum, comp) => {
                return sum + (parseFloat(comp.distance_miles) || 0);
            }, 0);
            return `${total.toFixed(1)} mi`;
        },

        calculateTotalDuration(tour) {
            if (!tour.tour_comps) return '0h';
            const totalMinutes = tour.tour_comps.reduce((sum, comp) => {
                return sum + (parseInt(comp.duration_minutes) || 0) + (parseInt(comp.drive_time_minutes) || 0);
            }, 0);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        },
    },
};
</script>
